import React, { useState, useEffect, useCallback } from 'react';
import { fetchCommandCenterData, AUTO_REFRESH_SECONDS } from '../../operations/jiraService';
import {
  CommandCenterData,
  CommandCenterIssue,
  SprintHealth,
  Assignee,
  FlowData,
} from './types';

const Z = {
  purple: "#6c00f6", lime: "#a1f919", cyan: "#00b8ff",
  bg: "#F7F7F8", card: "#FFFFFF", border: "#E8E8EC", borderLight: "#F0F0F3",
  text: "#1A1A2E", sec: "#666680", mute: "#9999AD", faint: "#BBBBC8",
  red: "#DC2626", orange: "#EA580C", green: "#16a34a",
};
const font = "'Plus Jakarta Sans', sans-serif";
const mono = "'JetBrains Mono', monospace";
const PC: Record<string, string> = { Highest: Z.red, High: Z.orange, Medium: Z.cyan, Low: Z.mute, None: Z.mute };
const ACOLS = [Z.purple, Z.cyan, "#E879F9", "#F59E0B", Z.green, "#6366F1", "#EC4899", "#14B8A6", "#8B5CF6", "#F97316"];

// ═══════════════ CHART COMPONENTS ═══════════════

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface GroupedBarsProps {
  data: Array<Record<string, any>>;
  k1: string;
  k2: string;
  c1: string;
  c2: string;
  avgLine?: number;
  label1?: string;
  label2?: string;
}

function GroupedBars({ data, k1, k2, c1, c2, avgLine, label1, label2 }: GroupedBarsProps) {
  const W = 420, H = 180, P = { l: 32, r: 10, t: 15, b: 28 };
  const cW = W - P.l - P.r, cH = H - P.t - P.b;
  const max = Math.max(...data.flatMap(d => [(d[k1] as number) || 0, (d[k2] as number) || 0]), 1);
  const n = data.length || 1;
  const gW = cW / n, bW = Math.min(14, gW * 0.32);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%" }}>
      {[0, 25, 50, 75, 100].map(pct => {
        const y = P.t + cH * (1 - pct / 100);
        return <g key={pct}><line x1={P.l} x2={W - P.r} y1={y} y2={y} stroke={Z.borderLight} strokeWidth={.5} />
          <text x={P.l - 4} y={y + 3} textAnchor="end" fontSize={7} fill={Z.mute} fontFamily={mono}>{Math.round(max * pct / 100)}</text></g>;
      })}
      {data.map((d, i) => {
        const gx = P.l + i * gW + gW / 2;
        const h1 = (((d[k1] as number) || 0) / max) * cH, h2 = (((d[k2] as number) || 0) / max) * cH;
        return <g key={i}>
          <rect x={gx - bW - 1} y={P.t + cH - h1} width={bW} height={Math.max(h1, 0)} fill={c1} rx={2} opacity={.4} />
          <rect x={gx + 1} y={P.t + cH - h2} width={bW} height={Math.max(h2, 0)} fill={c2} rx={2} />
          <text x={gx} y={H - P.b + 12} textAnchor="middle" fontSize={7} fill={Z.mute} fontFamily={mono}>{(d.name || d.label) as string}</text>
        </g>;
      })}
      {avgLine != null && avgLine > 0 && <>
        <line x1={P.l} x2={W - P.r} y1={P.t + cH - (avgLine / max) * cH} y2={P.t + cH - (avgLine / max) * cH} stroke={Z.cyan} strokeDasharray="4 2" strokeWidth={1} />
        <text x={W - P.r} y={P.t + cH - (avgLine / max) * cH - 4} textAnchor="end" fontSize={7} fill={Z.cyan} fontFamily={mono}>avg {avgLine}</text>
      </>}
      {label1 && <g>
        <rect x={P.l} y={2} width={8} height={8} fill={c1} rx={1} opacity={.4} />
        <text x={P.l + 11} y={9} fontSize={7} fill={Z.mute}>{label1}</text>
        <rect x={P.l + 70} y={2} width={8} height={8} fill={c2} rx={1} />
        <text x={P.l + 81} y={9} fontSize={7} fill={Z.mute}>{label2}</text>
      </g>}
    </svg>
  );
}

interface BurndownChartProps {
  health: SprintHealth;
}

function BurndownChart({ health }: BurndownChartProps) {
  if (!health) return null;
  const W = 300, H = 160, P = { l: 32, r: 10, t: 10, b: 25 };
  const cW = W - P.l - P.r, cH = H - P.t - P.b;
  const { totalDays, daysElapsed, totalSP, doneSP } = health;
  const remaining = totalSP - doneSP;
  const idealToday = totalSP * (1 - daysElapsed / totalDays);
  const behindOrAhead = remaining <= idealToday ? "ahead" : "behind";
  const ix = (d: number) => P.l + (d / totalDays) * cW;
  const iy = (sp: number) => P.t + cH - (sp / Math.max(totalSP, 1)) * cH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%" }}>
      {[0, 50, 100].map(pct => {
        const y = iy(totalSP * pct / 100);
        return <g key={pct}><line x1={P.l} x2={W - P.r} y1={y} y2={y} stroke={Z.borderLight} strokeWidth={.5} />
          <text x={P.l - 4} y={y + 3} textAnchor="end" fontSize={7} fill={Z.mute} fontFamily={mono}>{Math.round(totalSP * pct / 100)}</text></g>;
      })}
      <line x1={ix(0)} y1={iy(totalSP)} x2={ix(totalDays)} y2={iy(0)} stroke={Z.mute} strokeDasharray="4 2" strokeWidth={1} />
      <text x={ix(totalDays) - 2} y={iy(0) - 4} textAnchor="end" fontSize={7} fill={Z.mute}>ideal</text>
      <line x1={ix(0)} y1={iy(totalSP)} x2={ix(daysElapsed)} y2={iy(remaining)} stroke={behindOrAhead === "ahead" ? Z.green : Z.red} strokeWidth={2} />
      <circle cx={ix(daysElapsed)} cy={iy(remaining)} r={4} fill={behindOrAhead === "ahead" ? Z.green : Z.red} />
      <text x={ix(daysElapsed) + 6} y={iy(remaining) + 3} fontSize={8} fill={behindOrAhead === "ahead" ? Z.green : Z.red} fontWeight={700}>{remaining} SP</text>
      <line x1={ix(daysElapsed)} y1={P.t} x2={ix(daysElapsed)} y2={P.t + cH} stroke={Z.purple} strokeDasharray="2 2" strokeWidth={.5} opacity={.5} />
      <text x={ix(daysElapsed)} y={H - P.b + 12} textAnchor="middle" fontSize={7} fill={Z.purple} fontFamily={mono}>today</text>
      <text x={ix(0)} y={H - P.b + 12} textAnchor="start" fontSize={7} fill={Z.mute} fontFamily={mono}>day 1</text>
      <text x={ix(totalDays)} y={H - P.b + 12} textAnchor="end" fontSize={7} fill={Z.mute} fontFamily={mono}>day {totalDays}</text>
    </svg>
  );
}

interface DonutSegment {
  value: number;
  color: string;
}

interface DonutProps {
  segments: DonutSegment[];
  size?: number;
}

function Donut({ segments, size = 120 }: DonutProps) {
  const cx = size / 2, cy = size / 2, r = (size / 2) - 8, inner = r * 0.6;
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  let cum = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {segments.filter(s => s.value > 0).map((seg, i) => {
        const start = (cum / total) * 2 * Math.PI - Math.PI / 2;
        cum += seg.value;
        const end = (cum / total) * 2 * Math.PI - Math.PI / 2;
        const large = end - start > Math.PI ? 1 : 0;
        const d = `M ${cx + inner * Math.cos(start)} ${cy + inner * Math.sin(start)} L ${cx + r * Math.cos(start)} ${cy + r * Math.sin(start)} A ${r} ${r} 0 ${large} 1 ${cx + r * Math.cos(end)} ${cy + r * Math.sin(end)} L ${cx + inner * Math.cos(end)} ${cy + inner * Math.sin(end)} A ${inner} ${inner} 0 ${large} 0 ${cx + inner * Math.cos(start)} ${cy + inner * Math.sin(start)} Z`;
        return <path key={i} d={d} fill={seg.color} />;
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize={16} fontWeight={800} fill={Z.text} fontFamily={font}>{total}</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fontSize={8} fill={Z.mute} fontFamily={mono}>issues</text>
    </svg>
  );
}

interface FlowBarsProps {
  flow: FlowData;
}

function FlowBars({ flow }: FlowBarsProps) {
  const total = (flow.todo + flow.active + flow.review + flow.done) || 1;
  const items = [
    { label: "To Do", value: flow.todo, color: Z.mute },
    { label: "In Progress", value: flow.active, color: Z.orange },
    { label: "In Review", value: flow.review, color: Z.cyan },
    { label: "Done", value: flow.done, color: Z.purple },
  ];
  return (
    <div>
      <div style={{ display: "flex", gap: 2, height: 24, borderRadius: 6, overflow: "hidden", marginBottom: 10 }}>
        {items.map(s => s.value > 0 ? <div key={s.label} style={{ width: `${(s.value / total) * 100}%`, background: s.color, borderRadius: 4, transition: "width .6s ease", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 8, color: "#fff", fontWeight: 700, fontFamily: mono }}>{s.value}</span>
        </div> : null)}
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {items.map(s => <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
          <span style={{ fontSize: 9, color: Z.sec }}>{s.label}</span>
          <span style={{ fontSize: 9, fontFamily: mono, color: s.color, fontWeight: 600 }}>{s.value}</span>
        </div>)}
      </div>
    </div>
  );
}

interface RingProps {
  pct: number;
  color: string;
  sz?: number;
  sw?: number;
}

function Ring({ pct, color, sz = 56, sw = 5 }: RingProps) {
  const r = (sz - sw) / 2, C = 2 * Math.PI * r;
  return (
    <svg width={sz} height={sz} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" stroke={Z.borderLight} strokeWidth={sw} />
      <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" stroke={color} strokeWidth={sw}
        strokeDasharray={C} strokeDashoffset={C - (Math.min(pct, 100) / 100) * C} strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 1s cubic-bezier(.4,0,.2,1)" }} />
    </svg>
  );
}

// ═══════════════ SHARED UI ═══════════════

interface CardProps {
  title?: string;
  sub?: string;
  span?: number;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

const Card = ({ title, sub, span, children, style }: CardProps) => (
  <div style={{
    background: Z.card, border: `1px solid ${Z.border}`, borderRadius: 12,
    padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,.04)",
    gridColumn: span ? `span ${span}` : undefined, ...style,
  }}>
    {title && <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: Z.text }}>{title}</div>
      {sub && <div style={{ fontSize: 8, color: Z.mute, fontFamily: mono }}>{sub}</div>}
    </div>}
    {children}
  </div>
);

interface SectionHeaderProps {
  title: string;
  num: string;
}

const SectionHeader = ({ title, num }: SectionHeaderProps) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "20px 0 10px", padding: "0 2px" }}>
    <div style={{ width: 22, height: 22, borderRadius: 6, background: `${Z.purple}10`, color: Z.purple, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 }}>{num}</div>
    <div style={{ fontSize: 14, fontWeight: 800, color: Z.text }}>{title}</div>
    <div style={{ flex: 1, height: 1, background: Z.borderLight }} />
  </div>
);

interface IssueRowProps {
  issue: CommandCenterIssue;
}

const IssueRow = ({ issue }: IssueRowProps) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 0", borderBottom: `1px solid ${Z.borderLight}` }}>
    <span style={{ fontFamily: mono, fontSize: 8, color: Z.mute, minWidth: 50 }}>{issue.key}</span>
    <span style={{ fontSize: 7, color: PC[issue.priority] || Z.mute }}>●</span>
    <span style={{ fontSize: 10, color: Z.sec, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{issue.summary}</span>
    <span style={{ fontSize: 8, fontFamily: mono, color: Z.purple, background: `${Z.purple}08`, padding: "1px 6px", borderRadius: 3 }}>{issue.assignee || "\u2014"}</span>
  </div>
);

interface StatProps {
  label: string;
  value: string | number;
  color?: string;
  sub?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Stat = ({ label, value, color, sub }: StatProps) => (
  <div style={{ textAlign: "center", padding: 8 }}>
    <div style={{ fontSize: 7, fontFamily: mono, color: Z.mute, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: color || Z.text }}>{value}</div>
    {sub && <div style={{ fontSize: 8, color: Z.mute, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ═══════════════ MAIN DASHBOARD ═══════════════

export default function CommandCenter() {
  const [data, setData] = useState<CommandCenterData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(AUTO_REFRESH_SECONDS);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await fetchCommandCenterData();
      setData(result);
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : String(error));
    }
    setLoading(false);
    setCountdown(AUTO_REFRESH_SECONDS);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (loading || error) return;
    const t = setInterval(() => setCountdown(p => { if (p <= 1) { refresh(); return AUTO_REFRESH_SECONDS; } return p - 1; }), 1000);
    return () => clearInterval(t);
  }, [loading, error, refresh]);

  // Loading
  if (loading && !data) {
    return (
      <div style={{ minHeight: "100vh", background: Z.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: mono }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes pulse{0%,100%{opacity:.4}50%{opacity:1}}`}</style>
        <div style={{ width: 44, height: 44, border: `3px solid ${Z.borderLight}`, borderTop: `3px solid ${Z.purple}`, borderRadius: "50%", animation: "spin .9s linear infinite", marginBottom: 20 }} />
        <div style={{ color: Z.sec, fontSize: 12 }}>Loading Command Center...</div>
        <div style={{ color: Z.faint, fontSize: 10, marginTop: 6, animation: "pulse 2s ease infinite" }}>Fetching sprint, velocity & workload data</div>
      </div>
    );
  }

  // Error
  if (error && !data) {
    return (
      <div style={{ minHeight: "100vh", background: Z.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: font }}>
        <div style={{ fontSize: 28, marginBottom: 12 }}>{"\u26A0"}</div>
        <div style={{ color: Z.red, fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Connection Failed</div>
        <div style={{ color: Z.sec, fontSize: 12, maxWidth: 420, textAlign: "center", marginBottom: 16, lineHeight: 1.5 }}>{error}</div>
        <button onClick={refresh} style={{ padding: "8px 20px", borderRadius: 8, border: `1px solid ${Z.border}`, background: Z.card, color: Z.text, fontSize: 12, cursor: "pointer", fontFamily: mono }}>{"\u21BB"} Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const d = data;
  const sh = d.sprintHealth;
  const maxWL = Math.max(...d.assignees.map((a: Assignee) => a.sp), 1);
  const avgSP = d.assignees.length > 0 ? Math.round(d.assignees.reduce((s: number, a: Assignee) => s + a.sp, 0) / d.assignees.filter((a: Assignee) => a.name !== "Unassigned").length) : 0;

  return (
    <div style={{ minHeight: "100vh", background: Z.bg, color: Z.text, fontFamily: font }}>
      <style>{`*{box-sizing:border-box} ::-webkit-scrollbar{width:3px} ::-webkit-scrollbar-thumb{background:${Z.border};border-radius:2px}`}</style>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "16px 24px 40px" }}>

        {/* ═══ HEADER ═══ */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontSize: 8, fontFamily: mono, color: Z.faint, letterSpacing: 3, textTransform: "uppercase", marginBottom: 2 }}>Zenvoya</div>
            <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: "-.5px", background: `linear-gradient(135deg, ${Z.text} 40%, ${Z.purple})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Product Delivery Command Center
            </h1>
            <div style={{ fontSize: 10, color: Z.mute, fontFamily: mono, marginTop: 2 }}>
              {d.issues?.length || 0} active issues · Synced {new Date(d.timestamp).toLocaleTimeString()}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 6, background: `${Z.purple}08`, border: `1px solid ${Z.purple}20` }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: Z.purple, boxShadow: `0 0 6px ${Z.purple}66` }} />
              <span style={{ fontSize: 9, fontFamily: mono, color: Z.purple, fontWeight: 600 }}>LIVE</span>
              <span style={{ fontSize: 8, fontFamily: mono, color: `${Z.purple}80` }}>{Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, "0")}</span>
            </div>
            <button onClick={refresh} style={{ padding: "6px 14px", borderRadius: 6, border: `1px solid ${Z.border}`, background: Z.card, color: Z.sec, fontSize: 10, fontFamily: mono, cursor: "pointer" }}>{"\u21BB"} Refresh</button>
          </div>
        </div>

        {/* ═══ SECTION 1: Sprint Velocity & Productivity ═══ */}
        <SectionHeader num="1" title="Sprint Velocity & Productivity" />
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr", gap: 10 }}>
          <Card title="Velocity Chart" sub={`Avg: ${d.avgVelocity} SP`}>
            {d.velocity.length > 0
              ? <GroupedBars data={d.velocity} k1="committed" k2="completed" c1={Z.purple} c2={Z.purple} avgLine={d.avgVelocity} label1="Committed" label2="Completed" />
              : <div style={{ padding: 20, textAlign: "center", color: Z.mute, fontSize: 11 }}>No sprint history</div>}
          </Card>
          <Card title="Sprint Burndown" sub={sh ? `Day ${sh.daysElapsed}/${sh.totalDays}` : ""}>
            {sh ? <BurndownChart health={sh} /> : <div style={{ padding: 20, textAlign: "center", color: Z.mute, fontSize: 11 }}>No active sprint</div>}
          </Card>
          <Card title="Cycle Time" sub="Avg days to resolve">
            {Object.keys(d.avgCycleTimes).length > 0
              ? Object.entries(d.avgCycleTimes).map(([type, days]) => (
                <div key={type} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 9, color: Z.sec, minWidth: 50 }}>{type}</span>
                  <div style={{ flex: 1, height: 6, borderRadius: 3, background: Z.borderLight, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.min(((days as number) / 30) * 100, 100)}%`, background: Z.cyan, borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: 10, fontFamily: mono, fontWeight: 700, color: Z.text, minWidth: 24, textAlign: "right" }}>{days}d</span>
                </div>))
              : <div style={{ padding: 20, textAlign: "center", color: Z.mute, fontSize: 11 }}>No completed issues</div>}
          </Card>
        </div>

        {/* Cumulative Flow */}
        <div style={{ marginTop: 10 }}>
          <Card title="Status Distribution" sub="Current Sprint">
            <FlowBars flow={d.flowData} />
          </Card>
        </div>

        {/* ═══ SECTION 2: Current Sprint Highlights ═══ */}
        <SectionHeader num="2" title="Current Sprint Highlights" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
          <Card title="Sprint Health">
            {sh ? <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                <Ring pct={sh.pct} color={Z.purple} sz={60} sw={5} />
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: Z.purple }}>{sh.pct}%</div>
                  <div style={{ fontSize: 9, color: Z.mute }}>{sh.doneSP}/{sh.totalSP} SP</div>
                </div>
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: Z.text, marginBottom: 4 }}>{sh.name}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, fontSize: 9 }}>
                <div style={{ color: Z.sec }}>Days left: <b style={{ color: sh.daysRemaining <= 2 ? Z.red : Z.text }}>{sh.daysRemaining}</b></div>
                <div style={{ color: Z.sec }}>Total: <b>{sh.totalIssues}</b> issues</div>
                <div style={{ color: Z.sec }}>Done: <b style={{ color: Z.green }}>{sh.doneIssues}</b></div>
                <div style={{ color: Z.sec }}>Remaining: <b>{sh.remainingSP}</b> SP</div>
              </div>
            </div> : <div style={{ color: Z.mute, fontSize: 11, padding: 20, textAlign: "center" }}>No active sprint</div>}
          </Card>

          <Card title="Blocked / Highest" sub={`${d.blocked.length} items`}>
            <div style={{ maxHeight: 180, overflowY: "auto" }}>
              {d.blocked.length === 0 && <div style={{ color: Z.green, fontSize: 11, padding: 20, textAlign: "center" }}>No blockers</div>}
              {d.blocked.slice(0, 8).map((i: CommandCenterIssue) => <IssueRow key={i.key} issue={i} />)}
            </div>
          </Card>

          <Card title="Overdue" sub={`${d.overdue.length} items`}>
            <div style={{ maxHeight: 180, overflowY: "auto" }}>
              {d.overdue.length === 0 && <div style={{ color: Z.green, fontSize: 11, padding: 20, textAlign: "center" }}>All on time</div>}
              {d.overdue.slice(0, 8).map((i: CommandCenterIssue) => <IssueRow key={i.key} issue={i} />)}
            </div>
          </Card>

          <Card title="Recently Updated" sub="Last 24h">
            <div style={{ maxHeight: 180, overflowY: "auto" }}>
              {d.recentlyUpdated.length === 0 && <div style={{ color: Z.mute, fontSize: 11, padding: 20, textAlign: "center" }}>No recent activity</div>}
              {d.recentlyUpdated.map((i: CommandCenterIssue) => <IssueRow key={i.key} issue={i} />)}
            </div>
          </Card>
        </div>

        {/* ═══ SECTION 3: Workload Distribution ═══ */}
        <SectionHeader num="3" title="Workload Distribution" />
        <div style={{ display: "grid", gridTemplateColumns: "200px 1fr 1fr", gap: 10 }}>
          {/* Donut */}
          <Card title="Assignee Split">
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Donut segments={d.assignees.filter((a: Assignee) => a.name !== "Unassigned").map((a: Assignee, i: number) => ({ value: a.total, color: ACOLS[i % ACOLS.length] }))} size={130} />
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
                {d.assignees.filter((a: Assignee) => a.name !== "Unassigned").map((a: Assignee, i: number) => (
                  <div key={a.name} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 8 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 2, background: ACOLS[i % ACOLS.length], flexShrink: 0 }} />
                    <span style={{ color: Z.sec, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                    <span style={{ fontFamily: mono, color: Z.text, fontWeight: 600 }}>{a.total}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Heat Map */}
          <Card title="Status Heat Map" sub="Assignee × Status">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "4px 6px", color: Z.mute, fontWeight: 600, borderBottom: `1px solid ${Z.borderLight}` }}>Assignee</th>
                    {["To Do", "Active", "Review", "Done"].map(s => (
                      <th key={s} style={{ textAlign: "center", padding: "4px 6px", color: Z.mute, fontWeight: 600, borderBottom: `1px solid ${Z.borderLight}` }}>{s}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.assignees.map((a: Assignee, i: number) => (
                    <tr key={a.name}>
                      <td style={{ padding: "4px 6px", color: a.name === "Unassigned" ? Z.red : Z.text, fontWeight: 600, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", borderBottom: `1px solid ${Z.borderLight}` }}>{a.name}</td>
                      {[a.todo, a.active, a.review, a.done].map((v: number, j: number) => {
                        const colors = [Z.mute, Z.orange, Z.cyan, Z.purple];
                        return <td key={j} style={{
                          textAlign: "center", padding: "4px 6px", fontFamily: mono, fontWeight: 700,
                          color: v > 0 ? colors[j] : Z.faint,
                          background: v > 0 ? `${colors[j]}08` : "transparent",
                          borderBottom: `1px solid ${Z.borderLight}`,
                        }}>{v}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Workload Bars + Unassigned */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Card title="Story Points / Person" sub={`Avg: ${avgSP} SP`}>
              {d.assignees.filter((a: Assignee) => a.name !== "Unassigned").map((a: Assignee, i: number) => {
                const isHigh = a.sp > avgSP * 1.4;
                const isLow = a.sp < avgSP * 0.6 && avgSP > 0;
                return (
                  <div key={a.name} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 8, color: Z.sec, minWidth: 60, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name.split(" ")[0]}</span>
                    <div style={{ flex: 1, height: 8, borderRadius: 4, background: Z.borderLight, overflow: "hidden", position: "relative" }}>
                      <div style={{ height: "100%", width: `${(a.sp / maxWL) * 100}%`, background: isHigh ? Z.red : isLow ? Z.orange : Z.purple, borderRadius: 4, transition: "width .6s ease" }} />
                      {avgSP > 0 && <div style={{ position: "absolute", top: 0, left: `${(avgSP / maxWL) * 100}%`, width: 1, height: "100%", background: Z.text, opacity: .2 }} />}
                    </div>
                    <span style={{ fontSize: 9, fontFamily: mono, fontWeight: 700, color: isHigh ? Z.red : isLow ? Z.orange : Z.text, minWidth: 24, textAlign: "right" }}>{a.sp}</span>
                  </div>
                );
              })}
            </Card>
            <Card title="Unassigned" sub={`${d.unassigned.length} issues`} style={{ background: d.unassigned.length > 0 ? "#FEF2F2" : Z.card }}>
              {d.unassigned.length === 0
                ? <div style={{ color: Z.green, fontSize: 11, textAlign: "center", padding: 10 }}>All assigned</div>
                : <div style={{ maxHeight: 100, overflowY: "auto" }}>
                    {d.unassigned.slice(0, 5).map((i: CommandCenterIssue) => <div key={i.key} style={{ fontSize: 9, color: Z.sec, padding: "2px 0", borderBottom: `1px solid ${Z.borderLight}` }}>
                      <span style={{ fontFamily: mono, color: Z.mute, marginRight: 4 }}>{i.key}</span>{i.summary}
                    </div>)}
                    {d.unassigned.length > 5 && <div style={{ fontSize: 8, color: Z.mute, marginTop: 4 }}>+{d.unassigned.length - 5} more</div>}
                  </div>}
            </Card>
          </div>
        </div>

        {/* ═══ SECTION 4: Backlog & Pipeline Health ═══ */}
        <SectionHeader num="4" title="Backlog & Pipeline Health" />
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10 }}>
          <Card title="Created vs Resolved" sub="Last 4 weeks">
            <GroupedBars data={d.createdVsResolved} k1="created" k2="resolved" c1={Z.orange} c2={Z.purple} label1="Created" label2="Resolved" />
          </Card>
          <Card title="Priority Breakdown" sub="Open issues">
            {["Highest", "High", "Medium", "Low", "None"].filter(p => d.priorityBreakdown[p]).map(p => {
              const v = d.priorityBreakdown[p];
              const total = Object.values(d.priorityBreakdown).reduce((s: number, n) => s + (n as number), 0) || 1;
              return (
                <div key={p} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 1, background: PC[p], flexShrink: 0 }} />
                  <span style={{ fontSize: 9, color: Z.sec, minWidth: 48 }}>{p}</span>
                  <div style={{ flex: 1, height: 6, borderRadius: 3, background: Z.borderLight, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${(v / total) * 100}%`, background: PC[p], borderRadius: 3, opacity: .8 }} />
                  </div>
                  <span style={{ fontSize: 9, fontFamily: mono, fontWeight: 700, color: PC[p], minWidth: 20, textAlign: "right" }}>{v}</span>
                </div>
              );
            })}
          </Card>
          <Card title="Backlog" style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: d.backlog.count > 50 ? Z.orange : Z.text }}>{d.backlog.count}</div>
              <div style={{ fontSize: 9, color: Z.mute, lineHeight: 1.4 }}>items not in<br />any sprint</div>
            </div>
            <div style={{ maxHeight: 120, overflowY: "auto", flex: 1 }}>
              {d.backlog.issues.slice(0, 8).map((i: CommandCenterIssue) => (
                <div key={i.key} style={{ fontSize: 9, color: Z.sec, padding: "3px 0", borderBottom: `1px solid ${Z.borderLight}`, display: "flex", gap: 4 }}>
                  <span style={{ fontFamily: mono, color: Z.mute, flexShrink: 0 }}>{i.key}</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.summary}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* ═══ SECTION 5: Bug & Quality Tracking ═══ */}
        <SectionHeader num="5" title="Bug & Quality Tracking" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <Card title="Open Bugs by Severity" sub={`${d.bugs.total} total`}>
            {Object.keys(d.bugs.severity).length > 0
              ? <div>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
                    <Donut segments={["Highest", "High", "Medium", "Low", "None"].filter(p => d.bugs.severity[p]).map(p => ({ value: d.bugs.severity[p], color: PC[p] }))} size={110} />
                  </div>
                  {["Highest", "High", "Medium", "Low"].filter(p => d.bugs.severity[p]).map(p => (
                    <div key={p} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                      <span style={{ width: 6, height: 6, borderRadius: 1, background: PC[p] }} />
                      <span style={{ fontSize: 9, color: Z.sec, flex: 1 }}>{p}</span>
                      <span style={{ fontSize: 10, fontFamily: mono, fontWeight: 700, color: PC[p] }}>{d.bugs.severity[p]}</span>
                    </div>
                  ))}
                </div>
              : <div style={{ color: Z.green, fontSize: 11, padding: 20, textAlign: "center" }}>No open bugs</div>}
          </Card>

          <Card title="Bug Resolution Time" sub="Avg days (last 30d)">
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px 0" }}>
              <Ring pct={Math.min(d.bugResolutionTime * 3, 100)} color={d.bugResolutionTime > 14 ? Z.red : d.bugResolutionTime > 7 ? Z.orange : Z.green} sz={80} sw={6} />
              <div style={{ fontSize: 28, fontWeight: 800, color: d.bugResolutionTime > 14 ? Z.red : d.bugResolutionTime > 7 ? Z.orange : Z.green, marginTop: 8 }}>
                {d.bugResolutionTime || "\u2014"}
              </div>
              <div style={{ fontSize: 10, color: Z.mute }}>days average</div>
              <div style={{ fontSize: 8, color: Z.faint, marginTop: 4 }}>
                {d.bugResolutionTime <= 3 ? "Excellent" : d.bugResolutionTime <= 7 ? "Good" : d.bugResolutionTime <= 14 ? "Needs improvement" : "Critical"}
              </div>
            </div>
          </Card>

          <Card title="Recent Bugs" sub={`${d.bugs.issues.length} open`}>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {d.bugs.issues.length === 0 && <div style={{ color: Z.green, fontSize: 11, padding: 20, textAlign: "center" }}>No open bugs</div>}
              {d.bugs.issues.slice(0, 10).map((i: CommandCenterIssue) => (
                <div key={i.key} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 0", borderBottom: `1px solid ${Z.borderLight}` }}>
                  <span style={{ width: 5, height: 5, borderRadius: 1, background: PC[i.priority], flexShrink: 0 }} />
                  <span style={{ fontFamily: mono, fontSize: 8, color: Z.mute }}>{i.key}</span>
                  <span style={{ fontSize: 9, color: Z.sec, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.summary}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* ═══ QUICK FILTERS ═══ */}
        <SectionHeader num={"\u26A1"} title="Pre-built Filters (JQL)" />
        <Card>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              { name: "Active Sprint Open Issues", jql: "sprint in openSprints() AND status != Done" },
              { name: "Blocked Items", jql: 'priority = Highest AND sprint in openSprints()' },
              { name: "Unassigned Sprint Work", jql: "assignee is EMPTY AND sprint in openSprints()" },
              { name: "Overdue Issues", jql: "due < now() AND status != Done" },
            ].map(f => (
              <div key={f.name} style={{ padding: "8px 12px", borderRadius: 8, background: Z.bg, border: `1px solid ${Z.borderLight}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: Z.text, marginBottom: 2 }}>{f.name}</div>
                <div style={{ fontSize: 8, fontFamily: mono, color: Z.mute, wordBreak: "break-all" }}>{f.jql}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Footer */}
        <div style={{ marginTop: 24, padding: "12px 0", borderTop: `1px solid ${Z.border}`, display: "flex", justifyContent: "space-between" }}>
          <div style={{ fontSize: 9, color: Z.faint, fontFamily: mono }}>Zenvoya · Product Delivery Command Center v1.0</div>
          <div style={{ fontSize: 9, color: Z.faint, fontFamily: mono }}>Auto-refresh every {AUTO_REFRESH_SECONDS / 60}m · Jira REST + Agile API</div>
        </div>
      </div>
    </div>
  );
}
